import { useEffect, useState } from "react";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { PUBLIC_BASE } from "../lib/publicBase";
import { toast } from "sonner";
import { Sparkles, Moon, BookOpen, Loader2, Trash2, Send, Share2, Lock, CreditCard, Check } from "lucide-react";

const TABS = [
  { id: "tarot",  label: "Tarot",        icon: Sparkles },
  { id: "dream",  label: "Dream Journal", icon: BookOpen },
  { id: "moon",   label: "Moon & Astro",  icon: Moon },
];

export default function Oracle() {
  const [tab, setTab] = useState("tarot");
  const [pass, setPass] = useState(null);
  const [loadingPass, setLoadingPass] = useState(true);
  const [checkingOut, setCheckingOut] = useState(false);

  const loadPass = async () => {
    setLoadingPass(true);
    try {
      const { data } = await api.get("/oracle/day-pass/status");
      setPass(data);
    } catch (e) { setPass({ active: false }); }
    finally { setLoadingPass(false); }
  };
  useEffect(() => { loadPass(); }, []);

  // If the user just returned from Stripe checkout, the URL has session_id —
  // poll the status endpoint until the webhook processes it.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("session_id");
    if (!sid) return;
    let tries = 0;
    const i = setInterval(async () => {
      tries += 1;
      try {
        const { data } = await api.get(`/checkout/status/${sid}`);
        if (data.payment_status === "paid") {
          clearInterval(i);
          window.history.replaceState({}, "", "/oracle");
          toast.success("Oracle unlocked for today");
          loadPass();
        }
      } catch (e) { /* keep polling */ }
      if (tries > 20) clearInterval(i);
    }, 1500);
    return () => clearInterval(i);
  }, []);

  const buyPass = async () => {
    setCheckingOut(true);
    try {
      const { data } = await api.post("/checkout/init", {
        kind: "oracle_day_pass",
        origin: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't start checkout");
      setCheckingOut(false);
    }
  };

  if (loadingPass) {
    return <Layout><div className="text-foreground/55 italic">Reading the threshold…</div></Layout>;
  }

  if (!pass?.active) {
    return (
      <Layout>
        <div className="space-y-8" data-testid="oracle-paywall">
          <div>
            <div className="label-mystic">AI Spiritual Assistant</div>
            <h1 className="font-serif text-5xl mt-2">The Oracle</h1>
            <p className="text-foreground/65 mt-3 max-w-2xl">
              Three quiet companions for your daily practice — Daily Tarot,
              Dream Journal, and Moon & Astro timing. Unlock all three for the
              day with a single pass.
            </p>
          </div>

          <div className="parchment p-8 max-w-xl space-y-6" data-testid="day-pass-card">
            <div className="flex items-baseline gap-3">
              <Lock size={20} className="text-secondary" />
              <h2 className="font-serif text-3xl">Today's Oracle Pass</h2>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="font-serif text-6xl text-secondary">$4<span className="text-3xl">.99</span></span>
              <span className="text-xs text-foreground/55 uppercase tracking-[0.22em]">/ day · {pass?.day}</span>
            </div>
            <ul className="space-y-2 text-sm text-foreground/85">
              <li className="flex items-start gap-2"><Check size={14} className="text-primary mt-0.5"/> Unlimited tarot draws (one spread, re-readable all day)</li>
              <li className="flex items-start gap-2"><Check size={14} className="text-primary mt-0.5"/> Unlimited dream interpretations with symbol breakdowns</li>
              <li className="flex items-start gap-2"><Check size={14} className="text-primary mt-0.5"/> Today's moon phase + ritual timing guidance</li>
              <li className="flex items-start gap-2"><Check size={14} className="text-primary mt-0.5"/> Pass expires at midnight UTC — no auto-renew</li>
            </ul>
            <button onClick={buyPass} disabled={checkingOut}
              className="bg-primary text-primary-foreground px-8 py-4 uppercase tracking-[0.22em] text-xs rounded-sm hover:bg-primary/90 disabled:opacity-50 inline-flex items-center gap-2"
              data-testid="oracle-buy-pass-btn">
              {checkingOut ? <><Loader2 size={14} className="animate-spin"/> Redirecting…</> : <><CreditCard size={14}/> Unlock the Oracle · $4.99</>}
            </button>
            <div className="text-[10px] text-foreground/40 uppercase tracking-wider">
              Powered by Stripe · pass is non-transferable
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="label-mystic">AI Spiritual Assistant</div>
            <h1 className="font-serif text-5xl mt-2">The Oracle</h1>
            <p className="text-foreground/65 mt-3 max-w-2xl">
              Three quiet companions for your daily practice — open until midnight UTC.
            </p>
          </div>
          <div className="text-right" data-testid="pass-active-badge">
            <div className="label-mystic text-secondary">Today's pass · active</div>
            <div className="text-xs text-foreground/55 mt-1">{pass.day}</div>
          </div>
        </div>

        <div className="flex gap-1 border-b border-secondary/15 overflow-x-auto" data-testid="oracle-tabs">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button key={t.id} onClick={() => setTab(t.id)}
                className={`px-5 py-3 text-xs uppercase tracking-[0.22em] whitespace-nowrap inline-flex items-center gap-2 ${tab===t.id ? "text-secondary border-b-2 border-secondary" : "text-foreground/55 hover:text-foreground/85"}`}
                data-testid={`oracle-tab-${t.id}`}>
                <Icon size={12} /> {t.label}
              </button>
            );
          })}
        </div>

        {tab === "tarot" && <TarotPanel />}
        {tab === "dream" && <DreamPanel />}
        {tab === "moon"  && <MoonPanel />}
      </div>
    </Layout>
  );
}


// ============================================================
// Tarot
// ============================================================
function TarotPanel() {
  const [today, setToday] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [question, setQuestion] = useState("");

  const load = async () => {
    try {
      const [t, h] = await Promise.all([
        api.get("/oracle/tarot/today"),
        api.get("/oracle/tarot/history?limit=10"),
      ]);
      setToday(t.data?.pulled === false ? null : t.data);
      setHistory(h.data || []);
    } catch (e) { /* ignore — first-time users get null */ }
  };
  useEffect(() => { load(); }, []);

  const draw = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/oracle/tarot/draw", { question: question || null });
      setToday(data);
      toast.success("Cards drawn");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Draw failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-8" data-testid="tarot-panel">
      {!today ? (
        <div className="parchment p-8 space-y-5">
          <div>
            <div className="label-mystic">Daily Spread</div>
            <h2 className="font-serif text-3xl mt-1">Draw your three cards</h2>
            <p className="text-foreground/65 mt-2">Past · Present · Future. One draw per day.</p>
          </div>
          <input
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="Optional — what would you like the cards to weigh in on?"
            maxLength={240}
            className="w-full bg-background border border-secondary/30 px-4 py-3 text-foreground rounded-sm placeholder:text-foreground/35"
            data-testid="tarot-question-input"
          />
          <button onClick={draw} disabled={loading}
            className="bg-primary text-primary-foreground px-8 py-3 uppercase tracking-[0.2em] text-xs rounded-sm hover:bg-primary/90 disabled:opacity-50 inline-flex items-center gap-2"
            data-testid="tarot-draw-btn">
            {loading ? <><Loader2 size={14} className="animate-spin"/> Consulting the deck...</> : <><Sparkles size={14}/> Draw cards</>}
          </button>
        </div>
      ) : (
        <TarotSpread pull={today} />
      )}

      {history.length > 1 && (
        <div className="space-y-3" data-testid="tarot-history">
          <div className="label-mystic">Recent pulls</div>
          {history.slice(1).map((p) => (
            <details key={p.id} className="parchment p-4 cursor-pointer">
              <summary className="flex items-center justify-between">
                <span className="text-foreground/85 font-serif">{p.interpretation?.headline || "Daily spread"}</span>
                <span className="text-xs text-foreground/45">{p.day}</span>
              </summary>
              <div className="mt-4">
                <TarotSpread pull={p} compact />
              </div>
            </details>
          ))}
        </div>
      )}
    </div>
  );
}

function TarotSpread({ pull, compact = false }) {
  const i = pull.interpretation || {};
  const [shareLoading, setShareLoading] = useState(false);
  const [shareUrl, setShareUrl] = useState(pull.shareToken ? `/oracle/share/${pull.shareToken}` : null);

  const generateShare = async () => {
    setShareLoading(true);
    try {
      const { data } = await api.post(`/oracle/tarot/${pull.id}/share-card`);
      setShareUrl(data.shareUrl);
      toast.success("Share card ready");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't make a share card");
    } finally { setShareLoading(false); }
  };

  const copyLink = async () => {
    const fullUrl = `${PUBLIC_BASE}${shareUrl}`;
    try {
      await navigator.clipboard.writeText(fullUrl);
      toast.success("Link copied");
    } catch (e) {
      toast.error("Couldn't copy — long-press the link instead");
    }
  };

  return (
    <div className={`space-y-6 ${compact ? "" : "parchment p-8"}`} data-testid="tarot-spread">
      {!compact && (
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="label-mystic">Today · {pull.day}</div>
            <h2 className="font-serif text-3xl mt-1 italic text-secondary">{i.headline || "A turning of the wheel."}</h2>
          </div>
          {!compact && (
            shareUrl ? (
              <div className="flex items-center gap-2" data-testid="tarot-share-ready">
                <a href={shareUrl} target="_blank" rel="noreferrer"
                  className="text-xs text-secondary underline uppercase tracking-wider" data-testid="tarot-share-link">View card</a>
                <button onClick={copyLink}
                  className="inline-flex items-center gap-1.5 border border-secondary/30 text-secondary px-3 py-1.5 rounded-sm uppercase tracking-wider text-[11px]"
                  data-testid="tarot-share-copy"><Share2 size={11}/> Copy link</button>
              </div>
            ) : (
              <button onClick={generateShare} disabled={shareLoading}
                className="inline-flex items-center gap-1.5 border border-secondary/30 text-secondary px-3 py-1.5 rounded-sm uppercase tracking-wider text-[11px] disabled:opacity-50"
                data-testid="tarot-share-btn">
                {shareLoading ? <><Loader2 size={11} className="animate-spin"/> Conjuring…</> : <><Share2 size={11}/> Share card</>}
              </button>
            )
          )}
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {(pull.cards || []).map((c) => (
          <div key={c.position} className="border border-secondary/25 p-5 space-y-2 bg-secondary/[0.03]">
            <div className="label-mystic text-primary">{c.position}</div>
            <div className="font-serif text-2xl">{c.name}</div>
            <div className="text-xs text-foreground/55 italic">{c.keywords}</div>
            <div className="text-sm text-foreground/80 mt-3 leading-relaxed">
              {i[c.position] || "—"}
            </div>
          </div>
        ))}
      </div>
      {i.guidance && (
        <div className="border-l-2 border-primary/60 pl-4 text-sm text-foreground/85 italic">
          <span className="label-mystic block mb-1">Guidance</span>
          {i.guidance}
        </div>
      )}
    </div>
  );
}


// ============================================================
// Dream Journal
// ============================================================
function DreamPanel() {
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [list, setList] = useState([]);

  const load = async () => {
    try {
      const { data } = await api.get("/oracle/dreams?limit=20");
      setList(data || []);
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (text.length < 12) {
      toast.error("Tell me a little more about the dream");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/oracle/dream", { text });
      toast.success("Dream interpreted");
      setText("");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Interpretation failed");
    } finally { setLoading(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this dream entry?")) return;
    try {
      await api.delete(`/oracle/dreams/${id}`);
      toast.success("Dream removed");
      await load();
    } catch (e) { toast.error("Couldn't delete"); }
  };

  return (
    <div className="space-y-8" data-testid="dream-panel">
      <div className="parchment p-8 space-y-5">
        <div>
          <div className="label-mystic">Dream Journal</div>
          <h2 className="font-serif text-3xl mt-1">Tell me a dream</h2>
          <p className="text-foreground/65 mt-2">Write it while it's still warm. Symbols, feelings, fragments — anything counts.</p>
        </div>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={6}
          maxLength={4000}
          placeholder="I dreamt I was walking through a corridor of doors. Each door was a different color..."
          className="w-full bg-background border border-secondary/30 px-4 py-3 text-foreground rounded-sm placeholder:text-foreground/35 leading-relaxed"
          data-testid="dream-text-input"
        />
        <div className="flex items-center justify-between">
          <span className="text-xs text-foreground/45">{text.length} / 4000</span>
          <button onClick={submit} disabled={loading || text.length < 12}
            className="bg-primary text-primary-foreground px-6 py-2.5 uppercase tracking-[0.2em] text-xs rounded-sm hover:bg-primary/90 disabled:opacity-50 inline-flex items-center gap-2"
            data-testid="dream-submit-btn">
            {loading ? <><Loader2 size={12} className="animate-spin"/> Interpreting...</> : <><Send size={12}/> Interpret</>}
          </button>
        </div>
      </div>

      <div className="space-y-4" data-testid="dream-list">
        {list.map((d) => (
          <div key={d.id} className="parchment p-6 space-y-4" data-testid={`dream-${d.id}`}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="font-serif text-2xl italic text-secondary">{d.interpretation?.title || "Untitled dream"}</h3>
                <div className="text-[10px] text-foreground/45 uppercase tracking-wider mt-1">{new Date(d.createdAt).toLocaleString()}</div>
              </div>
              <button onClick={() => del(d.id)} className="text-foreground/40 hover:text-orange-300" data-testid={`dream-del-${d.id}`}>
                <Trash2 size={14} />
              </button>
            </div>
            <p className="text-sm text-foreground/70 italic border-l-2 border-secondary/30 pl-3">{d.text}</p>
            <div className="space-y-2">
              {(d.interpretation?.symbols || []).map((s, i) => (
                <div key={i} className="text-sm">
                  <span className="text-primary font-semibold">{s.symbol}</span>
                  <span className="text-foreground/70"> — {s.meaning}</span>
                </div>
              ))}
            </div>
            {d.interpretation?.synthesis && (
              <div className="text-sm text-foreground/85 leading-relaxed">
                {d.interpretation.synthesis}
              </div>
            )}
            {d.interpretation?.ritual && (
              <div className="border-l-2 border-primary/50 pl-3 text-sm text-foreground/85 italic">
                <span className="label-mystic block mb-1">Suggested ritual</span>
                {d.interpretation.ritual}
              </div>
            )}
          </div>
        ))}
        {list.length === 0 && (
          <div className="text-center text-foreground/45 italic font-serif text-lg py-10">
            No dreams yet. The journal is patient.
          </div>
        )}
      </div>
    </div>
  );
}


// ============================================================
// Moon & Astro
// ============================================================
function MoonPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/oracle/moon");
        setData(data);
      } catch (e) { /* ignore */ } finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="text-foreground/55 italic py-10 text-center">Reading the sky…</div>;
  if (!data) return <div className="text-foreground/55 italic py-10 text-center">The moon is hiding right now.</div>;

  const illum = Math.round((data.phase?.illumination || 0) * 100);

  return (
    <div className="space-y-6" data-testid="moon-panel">
      <div className="parchment p-8 space-y-6">
        <div className="flex items-start gap-6">
          <MoonGlyph illum={data.phase?.illumination || 0} />
          <div className="flex-1">
            <div className="label-mystic">Moon Phase · {data.day}</div>
            <h2 className="font-serif text-4xl mt-1">{data.phase?.phaseName}</h2>
            <div className="text-xs text-foreground/55 uppercase tracking-wider mt-2">
              {illum}% illuminated · {data.phase?.ageDays}d into cycle
            </div>
            <p className="font-serif italic text-secondary text-xl mt-4">{data.guidance?.vibe}</p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6 pt-2">
          <div>
            <div className="label-mystic text-primary">Favors</div>
            <ul className="mt-3 space-y-2">
              {(data.guidance?.favors || []).map((f, i) => (
                <li key={i} className="text-sm text-foreground/85 flex gap-2">
                  <span className="text-primary mt-1">✦</span>
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="label-mystic text-foreground/60">Defer if possible</div>
            <ul className="mt-3 space-y-2">
              {(data.guidance?.avoid || []).map((f, i) => (
                <li key={i} className="text-sm text-foreground/65 flex gap-2">
                  <span className="text-foreground/40 mt-1">◌</span>
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {data.guidance?.ritual && (
          <div className="border-l-2 border-primary/60 pl-4 text-sm text-foreground/85 italic">
            <span className="label-mystic block mb-1">Today's ritual</span>
            {data.guidance.ritual}
          </div>
        )}
      </div>
    </div>
  );
}

function MoonGlyph({ illum }) {
  // SVG moon — a lit disc with a shadow disc that slides across based on illum.
  const cx = 60, r = 50;
  // illum: 0 = new (full shadow), 1 = full (no shadow). For waxing/waning we
  // approximate by offset, but for a glyph just show shadow ellipse width.
  const ex = r * 2 * (1 - illum);
  return (
    <svg width="120" height="120" viewBox="0 0 120 120" data-testid="moon-glyph">
      <defs>
        <radialGradient id="moonLit" cx="40%" cy="40%">
          <stop offset="0%" stopColor="#F4EEDA" />
          <stop offset="100%" stopColor="#C5A059" />
        </radialGradient>
      </defs>
      <circle cx={cx} cy="60" r={r} fill="url(#moonLit)" />
      <ellipse cx={cx + ex / 2} cy="60" rx={Math.max(1, ex / 2)} ry={r} fill="#15102A" opacity="0.92" />
      <circle cx={cx} cy="60" r={r} fill="none" stroke="#D4B58C" strokeOpacity="0.4" />
    </svg>
  );
}
